import { ChevronRight, Loader2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import EmptyAvatar from '@/assets/icons/empty-avatar.svg?react';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input';
import Pagination from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGetZorClosedData } from '@/hooks/zor/useGetZorClosedData';
import { ClosedZeeUser } from '@/types/data-center/closed.type';

const CLOSED_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'territory', label: 'Territory Number' },
  { value: 'closed-date', label: 'Closed Date' },
];

const CLOSED_LIMIT_STORAGE_KEY = 'consultant_closed_tab_page_limit';
const CLOSED_LIMIT_DEFAULT = 20;

const getStoredLimit = (): number => {
  try {
    const stored = localStorage.getItem(CLOSED_LIMIT_STORAGE_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0
      ? parsed
      : CLOSED_LIMIT_DEFAULT;
  } catch {
    return CLOSED_LIMIT_DEFAULT;
  }
};

const ClosedTab = () => {
  const [searchVal, setSearchVal] = useState('');
  const [sort, setSort] = useState('closed-date');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getStoredLimit);
  const navigate = useNavigate();

  const { result, isLoading, isError, isRefetching } = useGetZorClosedData({
    page,
    limit,
  });

  const formatCompletedDate = (iso: string | undefined): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const closedUsers = Array.isArray(result?.data?.completed_zee_users)
    ? result.data.completed_zee_users
    : [];

  const total = result?.data?.total ?? 0;
  const totalPages = result?.data?.total_pages ?? 1;

  const filteredAndSortedUsers = useMemo(() => {
    const searchLower = searchVal.toLowerCase();
    let filtered = closedUsers.filter((user) => {
      const nameMatch = user.full_name?.toLowerCase().includes(searchLower);
      const emailMatch = user.email?.toLowerCase().includes(searchLower);
      const territoriesMatch = user.territories_display
        ?.toLowerCase()
        .includes(searchLower);
      const statesMatch = user.territory_states
        ?.join(', ')
        .toLowerCase()
        .includes(searchLower);
      return Boolean(
        nameMatch || emailMatch || territoriesMatch || statesMatch
      );
    });

    filtered = [...filtered].sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.full_name.localeCompare(b.full_name);
        case 'email':
          return a.email.localeCompare(b.email);
        case 'territory':
          return a.territories_display.localeCompare(b.territories_display);
        case 'closed-date':
        default:
          return (
            new Date(b.completed_at).getTime() -
            new Date(a.completed_at).getTime()
          );
      }
    });

    return filtered;
  }, [searchVal, sort, closedUsers]);

  const handleSortChange = (value: string) => {
    setSort(value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    try {
      localStorage.setItem(CLOSED_LIMIT_STORAGE_KEY, String(newLimit));
    } catch {
      // ignore storage errors
    }
  };

  const renderTableRow = (user: ClosedZeeUser) => {
    const territories = user.territories_display;
    const locationStates = user.territory_states?.join(', ');
    const closedDate = formatCompletedDate(user.completed_at);

    return (
      <TableRow key={user.zee_id}>
        <TableCell
          className="cursor-pointer px-4"
          onClick={() => {
            navigate(`/dashboard/zee-profile/${user.zee_id}`);
          }}
        >
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full border">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <EmptyAvatar className="h-8 w-8 rounded-full" />
              )}
            </div>
            <span>{user.full_name}</span>
          </div>
        </TableCell>
        <TableCell>{user.email}</TableCell>
        <TableCell>{territories}</TableCell>
        <TableCell>{locationStates}</TableCell>
        <TableCell>{closedDate}</TableCell>
        <TableCell className="px-4 text-right">
          <Button
            size="icon"
            type="button"
            variant="outline"
            onClick={() => {
              navigate(`/dashboard/zee-profile/${user.zee_id}`);
            }}
          >
            <ChevronRight />
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-red-500">Failed to load closed data</p>
          <p className="text-sm text-typo-gray-tertiary">
            Please try again later
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="as-strong as-title-01 flex items-center gap-2 border-b border-border p-4 text-zinc-700">
        Closed
        {isRefetching && <Loader2 className="size-4 animate-spin" />}
      </div>
      <div className="m-4 overflow-hidden rounded-xl border border-alpha-dark-5 bg-white">
        <div className="m-4 flex justify-between">
          <div className="relative flex items-center">
            <span className="absolute left-3 text-gray-500">
              <Search size={16} />
            </span>
            <Input
              type="text"
              value={searchVal}
              className="pl-8"
              placeholder="Search"
              onChange={handleSearchChange}
            />
          </div>
          <div className="flex gap-1">
            <Select onValueChange={handleSortChange} value={sort}>
              <SelectTrigger className="min-w-[200px]">
                <span className="text-typo-gray-tertiary">Sort By :</span>
                <SelectValue defaultValue={sort} />
              </SelectTrigger>
              <SelectContent>
                {CLOSED_SORT_OPTIONS.map((item) => (
                  <SelectItem value={item.value} key={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="m-4 overflow-hidden rounded-xl border border-alpha-dark-5 bg-muted">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="as-title-04 px-4">Customer</TableHead>
                <TableHead className="as-title-04">Email</TableHead>
                <TableHead className="as-title-04">Territory Number</TableHead>
                <TableHead className="as-title-04">
                  Franchise Location
                </TableHead>
                <TableHead className="as-title-04">Closed Date</TableHead>
                <TableHead className="as-title-04">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white text-typo-gray-secondary">
              {filteredAndSortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center">
                    <p className="text-typo-gray-tertiary">
                      No closed data found
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedUsers.map(renderTableRow)
              )}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 pb-4">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ClosedTab;
